<template>
  <div class="prof-page">
    <!-- ═══ APPBAR ═══ -->
    <header class="appbar">
      <router-link to="/" class="vm-mark">
        <img
          class="vm-logo"
          src="../assets/images/logo-videomedi.png"
          alt="Videomedi"
        />
      </router-link>
      <div
        id="appbar-menu"
        class="appbar-menu"
        :class="{ 'is-open': menuOpen }"
      >
        <nav class="appnav">
          <router-link to="/" exact active-class="is-active">
            Início
          </router-link>
          <router-link
            :to="{ path: '/', query: { buscar: '1' } }"
            active-class="is-active"
          >
            Buscar profissionais de saúde
          </router-link>
          <router-link v-if="loggedIn" to="/consultas" active-class="is-active">
            Minhas consultas
          </router-link>
          <router-link
            v-if="loggedIn"
            to="/documentos"
            active-class="is-active"
          >
            Documentos
          </router-link>
          <router-link
            v-if="loggedIn"
            to="/meus-pagamentos"
            active-class="is-active"
          >
            Pagamentos
          </router-link>
        </nav>
        <span class="appbar-spacer" />
        <div v-if="!loggedIn" class="appbar-auth">
          <router-link to="/login" class="btn btn--outline btn--sm">
            Entrar
          </router-link>
          <router-link to="/cadastrar" class="btn btn--primary btn--sm">
            Criar conta
          </router-link>
        </div>
      </div>
      <span class="appbar-icon">
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M10.3 21a1.94 1.94 0 0 0 3.4 0"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
          />
        </svg>
        <!-- <span class="dot" /> -->
      </span>
      <AppBarUserMenu v-if="loggedIn" />
      <button
        type="button"
        class="navbar-toggler appbar-toggler"
        :class="menuOpen ? 'not-collapsed' : 'collapsed'"
        aria-controls="appbar-menu"
        :aria-expanded="menuOpen ? 'true' : 'false'"
        :aria-label="menuOpen ? 'Fechar menu' : 'Abrir menu'"
        @click="menuOpen = !menuOpen"
      >
        <div class="animated-icon">
          <span />
          <span />
          <span />
          <span />
        </div>
      </button>
    </header>

    <!-- ═══ BREADCRUMB ═══ -->
    <div class="crumb-row">
      <router-link to="/">Início</router-link>
      <span class="sep">/</span>
      <router-link to="/busca">Buscar profissionais de saúde</router-link>
      <span class="sep">/</span>
      <a>
        {{ doctor.doctor_property.medical_specialty_name || "Especialidade" }}
      </a>
      <span class="sep">/</span>
      <span class="cur">{{ doctor.name }}</span>
    </div>

    <!-- ═══ CORPO: PERFIL + AGENDAMENTO ═══ -->
    <div class="prof-body">
      <!-- COLUNA PRINCIPAL -->
      <div class="prof-main">
        <!-- HEADER DO PERFIL -->
        <div class="card prof-head">
          <div
            class="avatar avatar--xl"
            :class="doctor.is_online ? 'avatar--ring' : ''"
            style="position:relative"
          >
            <img v-if="doctor.photo" :src="doctor.photo" :alt="doctor.name" />
            <template v-else>{{ doctorInitials }}</template>
            <span
              v-if="doctor.is_online"
              style="position:absolute;bottom:4px;right:4px;width:18px;height:18px;border-radius:50%;background:var(--color-success);border:3px solid #fff"
            />
          </div>
          <div class="pid">
            <div>
              <h1>{{ doctor.name }}</h1>
              <span
                v-if="
                  doctor.doctor_property.medical_specialty_name ||
                    doctor.doctor_property.council_description
                "
                class="role"
              >
                {{ doctor.doctor_property.medical_specialty_name }}
                <template
                  v-if="
                    doctor.doctor_property.medical_specialty_name &&
                      doctor.doctor_property.council_description
                  "
                >
                  ·
                </template>
                {{ doctor.doctor_property.council_description }}
                <template
                  v-if="
                    doctor.doctor_property.rqe &&
                      doctor.doctor_property.council_name &&
                      doctor.doctor_property.council_name.startsWith('CRM')
                  "
                >
                  · RQE {{ doctor.doctor_property.rqe }}
                </template>
              </span>
            </div>
            <div class="prof-badges">
              <span class="badge badge--success badge--no-dot">
                <svg
                  width="11"
                  height="11"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M12 2l8 3v6c0 5-3.5 8.5-8 11-4.5-2.5-8-6-8-11V5l8-3z"
                    stroke="currentColor"
                    stroke-width="2.5"
                    stroke-linejoin="round"
                  />
                </svg>
                Identidade verificada
              </span>
              <span v-if="hasRating" class="badge badge--neutral badge--no-dot">
                ★ {{ doctorRating }}
                <template v-if="testimonialCount > 0">
                  · {{ ratingCountLabel }}
                </template>
              </span>
              <span v-if="doctor.is_online" class="badge badge--success">
                Online hoje
              </span>
            </div>
          </div>
        </div>

        <!-- SOBRE -->
        <div v-if="doctor.doctor_property.short_bio" class="card prof-sec">
          <h2>Sobre {{ doctorFirstName }}</h2>
          <p class="body">{{ doctor.doctor_property.short_bio }}</p>
        </div>

        <!-- FORMAÇÃO E EXPERIÊNCIA -->
        <div
          class="card prof-sec"
          v-if="
            doctor.doctor_property.formation_entries &&
              doctor.doctor_property.formation_entries.length
          "
        >
          <h2>Formação e experiência</h2>
          <div class="prof-list">
            <div v-for="edu in sortedFormationEntries" :key="edu.id" class="li">
              <span class="yr">{{ edu.period }}</span>
              <div class="tx">
                <b>{{ edu.title }}</b>
                <span>{{ edu.institution }}</span>
              </div>
            </div>
          </div>
        </div>

        <div
          class="card prof-sec"
          v-if="
            doctor.doctor_property.condition_entries &&
              doctor.doctor_property.condition_entries.length
          "
        >
          <h2>Condições atendidas</h2>

          <div class="condition-entries-list">
            <span
              v-for="condition in sortedConditionEntries"
              :key="condition.id"
              class="condition-entries-list__item"
            >
              {{ condition.name }}
            </span>
          </div>
        </div>

        <!-- AVALIAÇÕES -->
        <div class="prof-sec">
          <h2>Avaliações de pacientes</h2>

          <div v-if="hasRating" class="card rev-summary">
            <div class="rev-score">
              <div class="big">{{ averageRating }}</div>
              <div class="st">{{ starsDisplay }}</div>
              <small v-if="testimonialCount > 0">{{ ratingCountLabel }}</small>
            </div>
            <div v-if="testimonialCount > 0" class="rev-bars">
              <div v-for="n in [5, 4, 3, 2, 1]" :key="n" class="rev-bar">
                <span class="lab">{{ n }}</span>
                <span class="track">
                  <i :style="{ width: ratingPercentage(n) + '%' }" />
                </span>
                <span>{{ ratingPercentage(n) }}%</span>
              </div>
            </div>
          </div>

          <div class="rev-grid">
            <template v-if="doctor.testimonials && doctor.testimonials.length">
              <div
                v-for="t in doctor.testimonials"
                :key="t.id"
                class="card rev-card"
              >
                <span class="stars-sm" style="color:#F59E0B">
                  {{ starsFor(t) }}
                </span>
                <p v-if="t.description" class="q">"{{ t.description }}"</p>
                <div v-if="t.tags && t.tags.length" class="rev-tags">
                  <span v-for="tag in t.tags" :key="tag" class="rev-tag">
                    {{ tagLabel(tag) }}
                  </span>
                </div>
                <div class="who">
                  <div class="avatar avatar--sm">
                    {{
                      t.patient_name
                        ? t.patient_name.substring(0, 2).toUpperCase()
                        : "?"
                    }}
                  </div>
                  <div class="stack" style="gap:0">
                    <span class="rev-nm">
                      {{ t.patient_name || "Paciente" }}
                    </span>
                    <span class="rev-dt">{{ formatDate(t.created_at) }}</span>
                  </div>
                </div>
              </div>
            </template>
            <div v-else class="card rev-empty">
              <p class="rev-empty__t">Ainda sem avaliações</p>
              <p class="rev-empty__s">
                As avaliações aparecem aqui depois das primeiras consultas.
              </p>
            </div>
          </div>
        </div>
      </div>
      <!-- /prof-main -->

      <!-- ═══ AGENDAMENTO SIDEBAR ═══ -->
      <aside class="card book-card">
        <div
          v-if="
            doctor.doctor_property.consultation_price_cents ||
              doctor.doctor_property.price_cents_and_earning_margin_sum
          "
          class="book-price"
        >
          <span class="v num">
            {{
              convertToBrl(
                doctor.doctor_property.consultation_price_cents ||
                  doctor.doctor_property.price_cents_and_earning_margin_sum
              )
            }}
          </span>
          <small>· por consulta</small>
        </div>

        <div v-if="availableSlots.length" class="book-next">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <circle
              cx="12"
              cy="12"
              r="9"
              stroke="currentColor"
              stroke-width="2"
            />
            <path
              d="M12 7v5l3 2"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
          Próximo horário: {{ nextSlotLabel }}
        </div>

        <div v-if="availableSlots.length">
          <div class="book-slot-day">{{ slotDayLabel }}</div>
          <div class="book-slots">
            <span
              v-for="slot in availableSlots"
              :key="slot.id"
              class="slot"
              :class="{
                'slot--active': isFirstSlot(slot),
                'slot--off': slot.status != null && slot.status !== 0
              }"
              @click="bookSlot(slot)"
            >
              {{ formatSlotTime(slot.start_time) }}
            </span>
          </div>
        </div>

        <button
          v-if="!availableSlots.length"
          class="btn btn--outline btn--block"
          :disabled="slotRequested || slotRequestLoading"
          @click="requestNewSlots"
        >
          {{
            slotRequested
              ? "Solicitação enviada ✓"
              : slotRequestLoading
              ? "Enviando..."
              : "Solicitar novos horários"
          }}
        </button>

        <button
          class="btn btn--accent btn--lg btn--block"
          @click="handleSchedule"
        >
          Agendar consulta
        </button>
        <button class="btn btn--outline btn--block" @click="handleSchedule">
          Ver agenda completa
        </button>

        <div class="book-trust">
          <span class="t">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <rect
                x="2"
                y="6"
                width="14"
                height="12"
                rx="2"
                stroke="currentColor"
                stroke-width="2"
              />
              <path
                d="M16 10l6-3v10l-6-3"
                stroke="currentColor"
                stroke-width="2"
                stroke-linejoin="round"
              />
            </svg>
            Consulta por vídeo · 30 min
          </span>
          <span class="t">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M20 6L9 17l-5-5"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            Cancelamento grátis até 24h antes
          </span>
          <span class="t">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M12 2l8 3v6c0 5-3.5 8.5-8 11-4.5-2.5-8-6-8-11V5l8-3z"
                stroke="currentColor"
                stroke-width="2"
                stroke-linejoin="round"
              />
            </svg>
            Pagamento seguro (Pix ou cartão)
          </span>
        </div>
      </aside>
    </div>
    <!-- /prof-body -->

    <!-- ═══ RODAPÉ ═══ -->
    <footer class="foot-mini">
      <span class="vm-mark">
        <img
          class="vm-logo vm-logo--sm"
          src="../assets/images/logo-videomedi.png"
          alt="Videomedi"
        />
      </span>
      <span class="cpy">© 2026 Videomedi · CNPJ 00.000.000/0001-00</span>
      <span class="links">
        <a href="#">Privacidade</a>
        <a href="#">Termos de Uso</a>
        <a href="#">LGPD</a>
        <a href="#">Suporte</a>
      </span>
    </footer>
  </div>
</template>

<script>
import { doctorMethods } from "./../state/helpers";
import { intToBRL } from "./../utils/currency/convert";
import { mapGetters, mapActions } from "vuex";
import AppBarUserMenu from "./../components/AppBarUserMenu.vue";

export default {
  name: "DoctorDetail",
  components: { AppBarUserMenu },

  data() {
    return {
      doctor: { doctor_property: {} },
      availableSlots: [],
      slotRequested: false,
      slotRequestLoading: false,
      menuOpen: false
    };
  },

  async mounted() {
    // Fecha a brecha de acesso direto pela URL: paciente logado e não
    // confirmado não pode ver o perfil do médico, mesmo sem passar pelos
    // botões da listagem (que já bloqueiam a navegação).
    if (this.loggedIn && !this.isEmailConfirmed) {
      this.$bvModal.show("email-confirmation-required");
      this.$router.replace({ name: "Home" });
      return;
    }

    const res = await this.getDoctorDetails(this.$route.params.doctor);
    this.doctor = res.data;
    if (!this.doctor.doctor_property) {
      this.doctor.doctor_property = {};
    }
    const dp = this.doctor.doctor_property;
    if (!dp.council_description) {
      dp.council_description = `${dp.council_name || ""} ${dp.council_number ||
        ""} ${dp.council_uf || ""}`.trim();
    }

    await this.loadSlots();
  },

  methods: {
    ...doctorMethods,
    ...mapActions("doctor", { fetchAvailables: "fetchDoctorsAvailables" }),
    ...mapActions("appointments", ["holdScheduleAppointment"]),
    ...mapActions("doctor", ["sendSMStoDoctor"]),

    async loadSlots() {
      try {
        const res = await this.fetchAvailables({ id: this.doctor.id });
        if (res?.data?.length) {
          const now = new Date();
          const expanded = [];
          res.data.forEach(daySlot => {
            if (!daySlot.slot_date || !Array.isArray(daySlot.slots)) return;
            daySlot.slots.forEach(timeStr => {
              const startTime = `${daySlot.slot_date}T${timeStr}:00`;
              if (new Date(startTime) > now) {
                expanded.push({
                  id: `${daySlot.id}_${timeStr}`,
                  slot_id: daySlot.id,
                  slot_date: daySlot.slot_date,
                  start_time: startTime
                });
              }
            });
          });
          if (expanded.length) {
            expanded.sort(
              (a, b) => new Date(a.start_time) - new Date(b.start_time)
            );
            const firstDay = expanded[0].slot_date;
            this.availableSlots = expanded
              .filter(s => s.slot_date === firstDay)
              .slice(0, 6);
          }
        }
      } catch {
        /* sem slots — fallback visual usado */
      }
    },

    convertToBrl(price) {
      return price ? intToBRL(price) : null;
    },

    async requestNewSlots() {
      if (!this.loggedIn) {
        this.$router.push({
          name: "ScheduleLogin",
          query: { redirectFrom: this.$route.fullPath }
        });
        return;
      }
      this.slotRequestLoading = true;
      try {
        await this.sendSMStoDoctor(this.doctor.id);
        this.slotRequested = true;
      } finally {
        this.slotRequestLoading = false;
      }
    },

    go(routeName) {
      this.$router.push({ name: routeName });
    },

    bookSlot(slot) {
      if (slot.status != null && slot.status !== 0) return;
      this.holdScheduleAppointment({
        slot_id: slot.slot_id,
        doctor_id: this.doctor.id,
        doctor: this.doctor,
        scheduled_at: slot.start_time
      });
      if (!this.loggedIn) {
        this.$router.push({ name: "ScheduleLogin" });
      } else {
        this.$router.push({ name: "Schedule" });
      }
    },

    handleSchedule() {
      this.$router.push({
        name: "SchedulePayment",
        params: { doctor: this.doctor.id }
      });
    },

    formatSlotTime(datetime) {
      if (!datetime) return "";
      return new Date(datetime).toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit"
      });
    },

    isFirstSlot(slot) {
      return (
        this.availableSlots.length > 0 && slot.id === this.availableSlots[0].id
      );
    },

    formatDate(dateStr) {
      if (!dateStr) return "";
      const diff = Math.floor((Date.now() - new Date(dateStr)) / 86400000);
      if (diff === 0) return "hoje";
      if (diff === 1) return "há 1 dia";
      if (diff < 7) return `há ${diff} dias`;
      if (diff < 14) return "há 1 semana";
      return `há ${Math.floor(diff / 7)} semanas`;
    },

    // Distribuição real. Sem avaliação, tudo zero — antes devolvia uma curva
    // inventada (92/6/1/1/0) que pintava barras num médico sem nota nenhuma.
    ratingPercentage(stars) {
      const list = this.ratedTestimonials;
      if (!list.length) return 0;
      const count = list.filter(t => t.stars === stars).length;
      return Math.round((count / list.length) * 100);
    },

    // Estrelas de um depoimento, na escala 1..5 que a API entrega em `stars`.
    starsFor(testimonial) {
      const n = Number(testimonial && testimonial.stars) || 0;
      return "★".repeat(n) + "☆".repeat(Math.max(0, 5 - n));
    },

    tagLabel(valor) {
      const rotulos = {
        pontualidade: "Pontualidade",
        atencao: "Atenção",
        boa_escuta: "Boa escuta",
        clareza: "Clareza",
        resolveu: "Resolveu meu problema"
      };
      return rotulos[valor] || valor;
    }
  },

  computed: {
    ...mapGetters("auth", ["loggedIn"]),
    ...mapGetters("user", ["getCurrentUser", "isEmailConfirmed"]),

    sortedFormationEntries() {
      if (!this.doctor.doctor_property.formation_entries) return [];

      return [...this.doctor.doctor_property.formation_entries].sort((a, b) => {
        return (a.position || 0) - (b.position || 0);
      });
    },

    sortedConditionEntries() {
      return this.doctor.doctor_property.condition_entries;
    },

    doctorInitials() {
      return (
        (this.doctor.name || "")
          .split(" ")
          .slice(0, 2)
          .map(n => n[0])
          .join("")
          .toUpperCase() || "?"
      );
    },
    doctorFirstName() {
      return (this.doctor.name || "")
        .split(" ")
        .slice(0, 2)
        .join(" ");
    },
    // Reputação vem calculada do servidor. Quem ainda não foi avaliado vem
    // com nota 5,0 e contagem 0; nesse caso a contagem não é exibida.
    hasRating() {
      return this.doctor.rating_average != null;
    },
    testimonialCount() {
      return this.doctor.rating_count || 0;
    },
    ratingCountLabel() {
      const n = this.testimonialCount;
      return `${n} ${n === 1 ? "avaliação" : "avaliações"}`;
    },
    doctorRating() {
      const media = this.doctor.rating_average;
      if (media === null || media === undefined) return null;
      return Number(media)
        .toFixed(1)
        .replace(".", ",");
    },
    averageRating() {
      return this.doctorRating;
    },
    // Depoimentos com nota utilizável, para a distribuição das barras.
    ratedTestimonials() {
      return (this.doctor.testimonials || []).filter(
        t => Number(t.stars) >= 1 && Number(t.stars) <= 5
      );
    },
    starsDisplay() {
      const media = Number(this.doctor.rating_average) || 0;
      const cheias = Math.round(media);
      return "★".repeat(cheias) + "☆".repeat(Math.max(0, 5 - cheias));
    },
    userInitials() {
      return (
        (this.getCurrentUser?.name || "")
          .split(" ")
          .slice(0, 2)
          .map(n => n[0])
          .join("")
          .toUpperCase() || "?"
      );
    },
    currentUserName() {
      return (this.getCurrentUser?.name || "")
        .split(" ")
        .slice(0, 2)
        .join(" ");
    },
    nextSlotLabel() {
      if (!this.availableSlots.length) return "em breve";
      const slot = this.availableSlots[0];
      const date = new Date(slot.start_time);
      const time = this.formatSlotTime(slot.start_time);
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);
      if (date.toDateString() === today.toDateString()) return `hoje, ${time}`;
      if (date.toDateString() === tomorrow.toDateString())
        return `amanhã, ${time}`;
      return `${date
        .toLocaleDateString("pt-BR", { weekday: "short" })
        .replace(".", "")}, ${time}`;
    },
    slotDayLabel() {
      const today = new Date();
      if (!this.availableSlots.length) {
        return `Hoje · ${today
          .toLocaleDateString("pt-BR", {
            weekday: "short",
            day: "numeric",
            month: "short"
          })
          .replace(".", "")}`;
      }
      const slot = this.availableSlots[0];
      const date = new Date(slot.start_time);
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);
      const dateLabel = date
        .toLocaleDateString("pt-BR", {
          weekday: "short",
          day: "numeric",
          month: "short"
        })
        .replace(".", "");
      if (date.toDateString() === today.toDateString())
        return `Hoje · ${dateLabel}`;
      if (date.toDateString() === tomorrow.toDateString())
        return `Amanhã · ${dateLabel}`;
      return dateLabel;
    }
  },

  watch: {
    $route() {
      this.menuOpen = false;
    }
  }
};
</script>

<style lang="scss" scoped>
.vm-logo {
  display: block;
  height: 28px;
  width: auto;
}

.vm-logo--sm {
  height: 22px;
}

/* ─── PAGE SHELL ─── */
.prof-page {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: var(--color-bg-page, #f4f6fb);
}

/* ─── APPBAR ─── */
.appbar {
  display: flex;
  align-items: center;
  gap: var(--space-8);
  background: #fff;
  border-bottom: 1px solid var(--color-gray-200);
  padding: 0 var(--space-8);
  height: 68px;
  position: sticky;
  top: 0;
  z-index: 10;
}

.vm-mark {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  text-decoration: none;

  svg {
    display: block;
  }
}

.vm-wordmark {
  font-size: var(--font-size-md);
  font-weight: var(--fw-extra);
  color: var(--color-accent);
  letter-spacing: -0.01em;
}

.appnav {
  display: flex;
  align-items: center;
  gap: var(--space-6);

  a {
    font-size: var(--font-size-sm);
    font-weight: var(--fw-semibold);
    color: var(--color-gray-600);
    text-decoration: none;
    cursor: pointer;

    &:hover {
      color: var(--color-primary);
    }
    &.is-active {
      color: var(--color-accent);
    }
    &.router-link-exact-active {
      color: var(--color-primary);
    }
  }
}

.appbar-spacer {
  flex: 1;
}

.appbar-menu {
  display: contents;
}

// Entrar/Criar conta ficam depois do sino no desktop, como antes do menu
.appbar-auth {
  order: 1;
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.appbar-toggler {
  order: 2;
  display: none;

  .animated-icon span {
    background: var(--color-gray-600) !important;
  }
}

@media (max-width: 991px) {
  .appbar {
    gap: var(--space-2);
    padding: 0 var(--space-4);
  }

  .appbar .vm-mark {
    margin-right: auto;
  }

  .appbar-toggler {
    display: block;
  }

  .appbar-menu {
    display: none;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    flex-direction: column;
    gap: var(--space-4);
    padding: var(--space-4);
    background: #fff;
    border-bottom: 1px solid var(--color-gray-200);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.06);

    &.is-open {
      display: flex;
    }
  }

  .appnav {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-4);
  }

  .appbar-spacer {
    display: none;
  }

  .appbar-auth {
    flex-direction: column;
    align-items: stretch;

    .btn {
      justify-content: center;
    }
  }
}

.appbar-icon {
  position: relative;
  width: 40px;
  height: 40px;
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-gray-500);
  cursor: pointer;

  &:hover {
    background: var(--color-gray-100);
    color: var(--color-gray-800);
  }

  .dot {
    position: absolute;
    top: 7px;
    right: 8px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--color-accent);
    border: 2px solid #fff;
  }
}

.appbar-user {
  display: flex;
  align-items: center;
  gap: var(--space-2);

  .nm {
    font-size: var(--font-size-sm);
    font-weight: var(--fw-bold);
    color: var(--color-gray-800);
  }
}

/* ─── BREADCRUMB ─── */
.crumb-row {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--font-size-sm);
  color: var(--color-gray-400);
  padding: var(--space-5) var(--space-10) 0;

  a {
    color: var(--color-gray-400);
    text-decoration: none;
    &:hover {
      color: var(--color-primary);
    }
  }

  .sep {
    color: var(--color-gray-300);
  }
  .cur {
    color: var(--color-gray-700);
    font-weight: var(--fw-semibold);
  }
}

/* ─── PROF BODY ─── */
.prof-body {
  display: grid;
  grid-template-columns: 1fr 340px;
  gap: var(--space-8);
  padding: var(--space-6) var(--space-10) var(--space-10);
  align-items: start;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    padding: var(--space-5);
  }
}

.prof-main {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

/* ─── PROF HEAD ─── */
.prof-head {
  display: flex;
  gap: var(--space-6);
  align-items: flex-start;
  padding: var(--space-6);
}

.pid {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);

  h1 {
    font-size: var(--font-size-2xl);
    font-weight: var(--fw-extra);
    color: var(--color-gray-900);
    letter-spacing: -0.01em;
    margin: 0 0 var(--space-1);
    line-height: 1.2;
  }

  .role {
    font-size: var(--font-size-md);
    color: var(--color-gray-500);
    display: block;
    margin-bottom: var(--space-1);
  }
}

.condition-entries-list {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  margin: 10px 0px 10px 0px;

  &__item {
    color: rgb(26, 26, 26);
    background-color: rgb(233, 233, 233);
    padding: 8px 17px 8px 17px;
    border-radius: 20px;
  }
}

.prof-badges {
  display: flex;
  gap: var(--space-2);
  flex-wrap: wrap;
}

/* ─── CARD: padding ausente no global do app — adicionado aqui ─── */
/* O .card global do app não tem padding; o handoff tem padding: space-6 */
.card {
  padding: var(--space-6);
}

/* ─── PROF SEC ─── */
.prof-sec {
  h2 {
    font-size: var(--font-size-lg);
    font-weight: var(--fw-bold);
    color: var(--color-gray-900);
    margin-bottom: var(--space-3);
  }

  p.body {
    font-size: var(--font-size-md);
    color: var(--color-gray-600);
    line-height: 1.65;
    margin: 0;
  }
}

/* ─── PROF LIST (formação) ─── */
.prof-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);

  .li {
    display: flex;
    gap: var(--space-4);

    .yr {
      font-family: var(--font-num, "Inter", sans-serif);
      font-size: var(--font-size-sm);
      font-weight: var(--fw-bold);
      color: var(--color-primary);
      white-space: nowrap;
      min-width: 76px;
    }

    .tx {
      b {
        display: block;
        font-size: var(--font-size-sm);
        color: var(--color-gray-900);
      }

      span {
        font-size: var(--font-size-sm);
        color: var(--color-gray-500);
      }
    }
  }
}

/* ─── AVALIAÇÕES ─── */
.rev-summary {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: var(--space-8);
  align-items: center;
  padding: var(--space-6);
  margin-bottom: var(--space-4);
}

.rev-score {
  text-align: center;

  .big {
    font-family: var(--font-num, "Inter", sans-serif);
    font-size: 3rem;
    font-weight: var(--fw-extra);
    color: var(--color-gray-900);
    line-height: 1;
  }

  .st {
    color: #f59e0b;
    font-size: var(--font-size-md);
  }

  small {
    display: block;
    font-size: var(--font-size-xs);
    color: var(--color-gray-400);
    margin-top: 4px;
  }
}

.rev-bars {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.rev-bar {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);

  .lab {
    width: 14px;
    text-align: right;
    font-family: var(--font-num, "Inter", sans-serif);
  }

  .track {
    flex: 1;
    height: 7px;
    background: var(--color-gray-100);
    border-radius: var(--radius-full);
    overflow: hidden;

    i {
      display: block;
      height: 100%;
      background: #f59e0b;
      border-radius: var(--radius-full);
    }
  }
}

.rev-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-4);

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
}

.rev-card {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-5);

  .stars-sm {
    display: inline-flex;
    gap: 1px;
    font-size: var(--font-size-sm);
  }

  .q {
    font-size: var(--font-size-sm);
    color: var(--color-gray-700);
    line-height: 1.6;
    flex: 1;
    margin: 0;
  }

  .who {
    display: flex;
    align-items: center;
    gap: var(--space-3);
  }

  .rev-nm {
    display: block;
    font-size: var(--font-size-sm);
    font-weight: var(--fw-bold);
    color: var(--color-gray-900);
  }

  .rev-dt {
    display: block;
    font-size: var(--font-size-xs);
    color: var(--color-gray-500);
  }

  .rev-tags {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
  }

  .rev-tag {
    font-size: var(--font-size-xs);
    color: var(--color-primary);
    background: var(--color-primary-light);
    border-radius: var(--radius-full);
    padding: 3px 10px;
  }
}

.rev-empty {
  grid-column: 1 / -1;
  padding: var(--space-8) var(--space-5);
  text-align: center;

  .rev-empty__t {
    font-size: var(--font-size-md);
    font-weight: var(--fw-bold);
    color: var(--color-gray-700);
    margin: 0 0 var(--space-1);
  }

  .rev-empty__s {
    font-size: var(--font-size-sm);
    color: var(--color-gray-500);
    margin: 0;
  }
}

/* ─── BOOK CARD (sidebar) ─── */
.book-card {
  position: sticky;
  top: var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  padding: var(--space-6);
  border-color: var(--color-primary) !important;

  @media (max-width: 860px) {
    position: static;
  }
}

.book-price {
  display: flex;
  align-items: baseline;
  gap: var(--space-2);

  .v {
    font-family: var(--font-num, "Inter", sans-serif);
    font-size: var(--font-size-3xl);
    font-weight: var(--fw-bold);
    color: var(--color-gray-900);
  }

  small {
    font-size: var(--font-size-sm);
    color: var(--color-gray-400);
  }
}

.book-next {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--font-size-sm);
  font-weight: var(--fw-bold);
  color: var(--color-success);
  background: var(--color-success-light, #dcfce7);
  border-radius: var(--radius-md);
  padding: var(--space-3) var(--space-4);
}

.book-slot-day {
  font-size: var(--font-size-xs);
  font-weight: var(--fw-bold);
  color: var(--color-gray-500);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: var(--space-2);
}

.book-slots {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-2);
}

/* slot component - definido localmente pois não está no SCSS global */
.slot {
  border: 1.5px solid var(--color-gray-200);
  background: #fff;
  color: var(--color-gray-700);
  border-radius: var(--radius-sm);
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-num, "Inter", sans-serif);
  font-size: var(--font-size-sm);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.12s;

  &:hover {
    border-color: var(--color-primary);
    color: var(--color-primary);
    background: var(--color-primary-light);
  }
}

.slot--active {
  background: var(--color-primary) !important;
  border-color: var(--color-primary) !important;
  color: #fff !important;
}
.slot--off {
  background: var(--color-gray-100) !important;
  color: var(--color-gray-300) !important;
  pointer-events: none;
}

.book-trust {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding-top: var(--space-2);
  border-top: 1px solid var(--color-gray-100);

  .t {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--font-size-xs);
    color: var(--color-gray-500);

    svg {
      color: var(--color-success);
      flex: none;
    }
  }
}

/* ─── FOOT MINI ─── */
.foot-mini {
  display: flex;
  align-items: center;
  gap: var(--space-5);
  padding: var(--space-5) var(--space-8);
  border-top: 1px solid var(--color-gray-200);
  background: var(--color-bg-page, #f4f6fb);
  margin-top: auto;

  .vm-mark {
    text-decoration: none;
  }
  .vm-wordmark {
    color: var(--color-gray-500);
    font-size: var(--font-size-sm);
    font-weight: normal;
  }
  .cpy {
    font-size: var(--font-size-xs);
    color: var(--color-gray-400);
  }
  .links {
    margin-left: auto;
    display: flex;
    gap: var(--space-5);

    a {
      font-size: var(--font-size-xs);
      font-weight: var(--fw-semibold);
      color: var(--color-gray-500);
      text-decoration: none;
      &:hover {
        color: var(--color-primary);
      }
    }
  }
}
</style>
